/**
 * How a group of photos and videos is laid out, as rows of equal-width tiles.
 *
 * The whole of it is a function of one number — how many items there are — which
 * is why it lives here rather than inside the bubble that draws it: the compose
 * screen and the sent message have to agree exactly, since the point of the
 * preview is that the sender sees the real grouping before pressing send. One
 * table, read by both.
 *
 * The shapes are the handoff's, and they are not a formula: a row of one over a
 * row of two for three items, a 2×2 for four, a pair over a triple for five. The
 * table is the specification.
 */

import type { OutgoingItem } from '../api/client.ts';

/** Mirrors MAX_ALBUM_ITEMS in server/src/chats.ts. */
export const MAX_ALBUM_ITEMS = 10;

/**
 * How many tiles are ever drawn. The tenth is what the «+1» badge on the ninth
 * stands for — and because the cap above is ten, the overflow is only ever
 * exactly one, which is what keeps that badge a footnote rather than a lid over
 * most of the message.
 */
const VISIBLE_MAX = 9;

/** The width the whole grid lays out at, from the handoff. */
export const ALBUM_WIDTH = 232;

/** The gap between tiles, which the thread's own background shows through. */
export const ALBUM_GAP = 2;

/** Row shapes by visible tile count: how many tiles in each row, and how tall. */
const ROWS_BY_COUNT: Record<number, { count: number; height: number }[]> = {
  1: [{ count: 1, height: 270 }],
  2: [{ count: 2, height: 150 }],
  3: [
    { count: 1, height: 150 },
    { count: 2, height: 110 },
  ],
  4: [
    { count: 2, height: 115 },
    { count: 2, height: 115 },
  ],
  5: [
    { count: 2, height: 115 },
    { count: 3, height: 76 },
  ],
  6: [
    { count: 3, height: 76 },
    { count: 3, height: 76 },
  ],
  7: [
    { count: 3, height: 76 },
    { count: 3, height: 76 },
    { count: 1, height: 76 },
  ],
  8: [
    { count: 3, height: 76 },
    { count: 3, height: 76 },
    { count: 2, height: 115 },
  ],
  9: [
    { count: 3, height: 76 },
    { count: 3, height: 76 },
    { count: 3, height: 76 },
  ],
};

export interface AlbumTile {
  /** Which item this draws — its position in the message, and its address. */
  index: number;
  width: number;
  height: number;
  /**
   * How many items this tile stands in for besides itself, drawn as «+N» over a
   * scrim. 0 on every tile but the last one of an over-long group.
   */
  overflow: number;
}

export interface AlbumRow {
  tiles: AlbumTile[];
}

/** The width of one tile in a row of `count`, so the row comes to ALBUM_WIDTH. */
function tileWidth(count: number): number {
  return Math.round((ALBUM_WIDTH - ALBUM_GAP * (count - 1)) / count);
}

/**
 * The grid for a group of `total` items — rows of tiles, top to bottom.
 *
 * Anything past nine collapses onto the ninth tile's «+N» rather than adding
 * rows, and anything past the cap is laid out as if it were exactly at it: a
 * count this cannot happen to is better than a layout that has to guess.
 */
export function albumRows(total: number): AlbumRow[] {
  if (total <= 0) return [];

  const visible = Math.min(total, VISIBLE_MAX);
  const overflow = total - visible;
  const shape = ROWS_BY_COUNT[visible] ?? ROWS_BY_COUNT[VISIBLE_MAX]!;

  let index = 0;
  return shape.map((row) => ({
    tiles: Array.from({ length: row.count }, () => {
      const tile: AlbumTile = {
        index,
        width: tileWidth(row.count),
        height: row.height,
        overflow: index === visible - 1 ? overflow : 0,
      };
      index += 1;
      return tile;
    }),
  }));
}

/**
 * What can still be added to a selection, given what is already in it.
 *
 * The cap is applied here rather than only by disabling something, because the
 * one way to reach it is picking more files than the cap in a single trip to the
 * system picker — where there is nothing of ours to disable.
 */
export function roomFor<T>(taken: number, incoming: T[]): { kept: T[]; dropped: number } {
  const room = Math.max(0, MAX_ALBUM_ITEMS - taken);
  return { kept: incoming.slice(0, room), dropped: Math.max(0, incoming.length - room) };
}

/**
 * Whether a file is something an album tile can hold.
 *
 * Mirrors the server's rule (albumAttachments in server/src/routes/chats.ts),
 * which is the one that counts — this is here so a document picked by mistake is
 * kept out of the compose screen rather than refused after the upload. SVG is
 * excluded on both sides: it is a document that can carry script, and this app
 * has never rendered one inline.
 */
export function albumKindOf(file: File): 'photo' | 'video' | null {
  const mime = file.type.toLowerCase();
  if (mime === 'image/svg+xml') return null;
  if (mime.startsWith('image/')) return 'photo';
  return mime.startsWith('video/') ? 'video' : null;
}

/**
 * A picked item while it is still on the compose screen.
 *
 * `OutgoingItem` is what the upload takes and it is complete the moment the
 * screen can send: a file, its frame, its length. These two fields are the
 * screen's own, and they exist because a video's frame and length are read on
 * this device and take a moment (see lib/videoFrame.ts) — the tiles go up first,
 * with a spinner where the frame will be, and each fills itself in when its clip
 * has been read. Which is the whole point: the compose screen appears when the
 * picker closes rather than several seconds later.
 */
export interface PickedItem extends OutgoingItem {
  /**
   * Which tile a frame that has just been read belongs to.
   *
   * Not the file and not the index: the same clip can be picked twice, and
   * anything already in the selection can be dropped while a later one is still
   * being read, which moves every index after it.
   */
  id: number;
  /** True until this item's frame and length have been read. Photos are never
      it — an object URL for the file is the picture. */
  reading: boolean;
}

let lastPickId = 0;

/** The next tile id. Unique within this session, which is as long as a compose
    screen ever lives. */
export function nextPickId(): number {
  lastPickId += 1;
  return lastPickId;
}
